import { Colors, delayColor, getRouteColor, getRouteTextColor, routeBadgeLabel } from '../../ui/colors'
import { canvasBulletFont } from '../../ui/theme'
import type { VehiclePosition } from '../../store/transit'
import { ROUTE_ALIAS } from './transitMapTracks'

// Convert a '#rrggbb' string to [R,G,B,A] once and cache the result. Called per vehicle
// per frame, so the parse must not happen more than once per distinct color.
export const rgbCache = new Map<string, [number, number, number, number]>()
export function hexRgb(hex: string): [number, number, number, number] {
  const cached = rgbCache.get(hex)
  if (cached) return cached
  const rgb: [number, number, number, number] = [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
    255,
  ]
  rgbCache.set(hex, rgb)
  return rgb
}

export function getRouteColorRgb(routeId: string): [number, number, number, number] {
  return hexRgb(getRouteColor(routeId))
}

/** The analysis layers that repaint vehicle bodies. See store/layers.ts. */
export interface VehicleTint {
  delays: boolean
}

/** True when an analysis layer has taken over vehicle color from the route palette. */
export function tintActive(tint: VehicleTint): boolean {
  return tint.delays
}

/**
 * The body color for one vehicle. Route color normally; when an analysis layer is on, the
 * derived-value color instead, so the map answers "how is the system doing" rather than
 * "which line is this". Buses carry no delay value, so they take the unknown gray rather
 * than being falsely reported as on time.
 */
export function vehicleColorRgb(v: VehiclePosition, tint: VehicleTint): [number, number, number, number] {
  if (tint.delays) return hexRgb(delayColor(v.delaySeconds))
  return getRouteColorRgb(v.routeId)
}

// Teal accent (Colors.primary) for the selected-station highlight: distinct from
// every MTA route color and the gray default stop dot.
export const SELECTED_COLOR = '#00d4aa'
// ⚠️ There is NO vehicle body tint, for any state — not watched, not boarded, not
// selected. Both tints that existed have been removed, and for one reason: `getColor`
// on a Scenegraph/SimpleMesh layer multiplies the ENTIRE model, so it cannot mark a
// vehicle without repainting it. Watch mode's amber went first (the camera is already
// locked on with the HUD up, so the tint disambiguated nothing); boarding's teal
// followed, because the boarded route is already drawn as its own highlighted line
// with its stops, the rider panel is open naming the next stop, and the camera follows.
//
// Keep it that way as the models gain real liveries. An MTA bus IS its color — blue
// ElectricBus, yellow clean-energy, standard local — so a flat tint over the body
// destroys the one cue that says which bus you are looking at. Anything that needs to
// mark a vehicle should be drawn BESIDE it (a ring, a line, a label), not multiplied
// through it.
// Hot magenta for the user's own location. Deliberately outside every MTA route color
// (reds/greens/blues/orange/yellow/purple/brown/grays), the dark-blue bus dot, and the
// teal selected-station accent, so "you" never reads as a train line or a station.
export const USER_LOCATION_COLOR = '#FF2D95'
// Vivid violet for the selected-bus-route highlight. Every bus route falls back to the
// same dark navy (Colors.bus), which on this map already means "bus infrastructure"
// (stop dots, moving-bus badges) and sits too close to the A/C/E blue to read as a
// selection; the other free accents are spoken for (teal = selected station, magenta =
// you). Violet is the one strong hue no subway line uses, so a highlighted bus route
// can never be mistaken for a train line.
export const SELECTED_BUS_ROUTE_COLOR = '#8B5CF6'

// Amber for the planned-trip overlay's walking legs and endpoint dots. Distinct from
// the three accents above and from every MTA route color (their yellow is #FCCC0A on
// N/Q/R/W ride segments, which render at route color, never amber).
export const ITINERARY_WALK_COLOR = '#FFB300'

/**
 * The single character an MTA bullet carries for a route id, which is two rules:
 * the express variants ride their local's glyph (the 7X's bullet says "7", in a
 * diamond), and anything still longer than two characters (the shuttles, `SI`) is
 * cut to its initial, because a bullet is one glyph and a third letter is unreadable
 * at the sizes these are drawn at. Every bullet rasteriser in the app goes through
 * here: the stop badge, the train badge, and the R160's own side and front signs.
 */
export function bulletGlyph(routeId: string): string {
  const base = routeBadgeLabel(ROUTE_ALIAS[routeId] ?? routeId)
  return base.length > 2 ? base[0] : base
}

export function injectRiderPulseCSS() {
  if (document.getElementById('rider-pulse-css')) return
  const s = document.createElement('style')
  s.id = 'rider-pulse-css'
  // left:50%;top:50% + translate(-50%,-50%) centers the ring inside its
  // fixed-size parent regardless of the parent's own position on the map.
  s.textContent = `
    @keyframes rider-pulse {
      0%   { transform: translate(-50%,-50%) scale(0.5); opacity: 1; }
      100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0; }
    }
    .rider-ring {
      position: absolute; left: 50%; top: 50%;
      width: 40px; height: 40px; border-radius: 50%;
      border: 3px solid currentColor; pointer-events: none;
      animation: rider-pulse 1.5s ease-out infinite;
    }
  `
  document.head.appendChild(s)
}

// Complex merging put every division's lines on one dot, so a badge now carries far more
// bullets than a single-division stop did: Times Sq/Port Authority is 13, a 206px strip
// beside a 14px dot in one row, so bullets wrap instead. A "+N" overflow cap (what
// createBusStopBadge does) is deliberately NOT used: hiding lines at a station is the exact
// bug complexes were merged to fix. 7 per row keeps every real station to two rows.
const STOP_BADGE_MAX_PER_ROW = 7

export function stopBadgeRows(routes: string[]): number {
  return Math.max(1, Math.ceil(routes.length / STOP_BADGE_MAX_PER_ROW))
}

export function createStopBadge(routes: string[]): ImageData {
  const D = 14, GAP = 2
  const rows = stopBadgeRows(routes)
  // Spread evenly rather than filling rows to the brim, so 8 routes read as 4+4, not 7+1.
  const perRow = Math.max(1, Math.ceil(routes.length / rows))
  const W = Math.max(1, perRow * D + Math.max(0, perRow - 1) * GAP)
  const H = rows * D + Math.max(0, rows - 1) * GAP
  const canvas = document.createElement('canvas')
  canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')!
  routes.forEach((routeId, i) => {
    const row = Math.floor(i / perRow), col = i % perRow
    // A short final row (13 = 7 + 6) is centred so the badge stays visually balanced.
    const inRow = Math.min(perRow, routes.length - row * perRow)
    const rowW = inRow * D + Math.max(0, inRow - 1) * GAP
    const cx = (W - rowW) / 2 + col * (D + GAP) + D / 2
    const cy = row * (D + GAP) + D / 2, r = D / 2
    ctx.fillStyle = getRouteColor(routeId)
    if (routeId.includes('X')) {
      ctx.beginPath()
      ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r, cy)
      ctx.lineTo(cx, cy + r); ctx.lineTo(cx - r, cy)
      ctx.closePath(); ctx.fill()
    } else {
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, 2 * Math.PI); ctx.fill()
    }
    ctx.fillStyle = getRouteTextColor(routeId)
    const label = routeBadgeLabel(routeId)
    ctx.font = canvasBulletFont(label.length === 1 ? 9 : 7)
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
    ctx.fillText(label, cx, cy + 0.5)
  })
  return ctx.getImageData(0, 0, W, H)
}

// Route pills shown at a zoomed-in bus stop: the bus analog of createStopBadge, the way
// a subway station shows the lines that stop there. Bus route ids are longer alphanumerics
// ("M15", "Bx12", "SIM1"), so each is a small rounded pill sized to its short name rather
// than a lettered disc. Drawn as a solid tan pill with black text, distinct from the
// moving-bus badge's blue-with-white-text, so a stop's lines read differently from a bus
// driving past. A stop can serve many lines, which would run off-screen, so it's capped: the
// first few are drawn and the rest collapse into a gray "+N" pill. Returns ImageData for
// map.addImage, keyed per unique route-set so it's built once and reused across stops.
export function createBusStopBadge(routes: string[]): ImageData {
  const MAX = 4, H = 16, PAD = 5, GAP = 2, RAD = 4, FONT = canvasBulletFont(10)
  // first MAX shown outright; if more, keep MAX-1 and append a "+N" overflow pill
  const shown = routes.length > MAX ? routes.slice(0, MAX - 1) : routes.slice(0, MAX)
  const overflow = routes.length - shown.length
  const labels = overflow > 0 ? [...shown, `+${overflow}`] : shown
  const measure = document.createElement('canvas').getContext('2d')!
  measure.font = FONT
  const widths = labels.map((l) => Math.ceil(measure.measureText(l).width) + PAD * 2)
  const W = Math.max(1, widths.reduce((a, b) => a + b, 0) + Math.max(0, labels.length - 1) * GAP)
  const canvas = document.createElement('canvas')
  canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d')!
  ctx.font = FONT
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  let x = 0
  labels.forEach((label, i) => {
    const w = widths[i]
    // Solid tan "posted sign" pill with black text, deliberately off the moving-bus
    // badge's blue-with-white-text so a stationary stop's lines can't be mistaken for a
    // bus driving past. (No overflow-specific tint; the "+N" pill matches the rest.)
    const left = x + 1, right = x + w - 1, top = 1, bottom = H - 1, rr = RAD - 1
    ctx.fillStyle = '#C79A54'
    ctx.beginPath()
    ctx.moveTo(left + rr, top)
    ctx.arcTo(right, top, right, bottom, rr)
    ctx.arcTo(right, bottom, left, bottom, rr)
    ctx.arcTo(left, bottom, left, top, rr)
    ctx.arcTo(left, top, right, top, rr)
    ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#000'
    ctx.fillText(label, x + w / 2, H / 2 + 0.5)
    x += w + GAP
  })
  return ctx.getImageData(0, 0, W, H)
}

export const trainBadgeUrlCache = new Map<string, string>()
export function createTrainBadgeUrl(routeId: string): string {
  const cached = trainBadgeUrlCache.get(routeId)
  if (cached) return cached
  const D = 28
  const scale = 2
  const canvas = document.createElement('canvas')
  canvas.width = D * scale; canvas.height = D * scale
  const ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)
  const cx = D / 2, cy = D / 2, r = D / 2
  const color = getRouteColor(routeId)
  ctx.fillStyle = color
  if (routeId.includes('X')) {
    ctx.beginPath()
    ctx.moveTo(cx, 0); ctx.lineTo(D, cy)
    ctx.lineTo(cx, D); ctx.lineTo(0, cy)
    ctx.closePath(); ctx.fill()
  } else {
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, 2 * Math.PI); ctx.fill()
  }
  const label = routeBadgeLabel(routeId)
  ctx.fillStyle = getRouteTextColor(routeId)
  ctx.font = canvasBulletFont(label.length === 1 ? 14 : 10)
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(label, cx, cy + 0.5)
  const url = canvas.toDataURL()
  trainBadgeUrlCache.set(routeId, url)
  return url
}

/**
 * ── The zoomed-out train marker ──────────────────────────────────────────────
 *
 * Below R160_MESH_MIN_ZOOM a car is a few pixels long: its livery, its decals and its
 * cab are all sub-pixel, and what is actually readable off the map at that size is only
 * which line the train is and which way it is going. So the lowest LOD is not a mesh at
 * all: it is the route bullet with this arrow (see r160LodForZoom).
 *
 * The arrow is a SEPARATE icon from the bullet because the two have to rotate
 * differently: the bullet is a screen-facing billboard whose glyph has to stay upright
 * to be read, and the arrow's whole job is to lie flat on the map and point along the
 * ground the way the train is travelling. (Same split the user-location dot makes:
 * viewport-aligned dot, map-aligned halo.)
 *
 * The canvas is mostly empty BELOW the dart and the icon is anchored at its bottom edge
 * (`anchorY = height`), so the dart is drawn entirely on the far side of the train's
 * position: rotating the icon to the bearing swings it around the bullet instead of
 * over it, at any map rotation, with no per-frame offset maths. The BARB row is what
 * keeps it off the bullet: it must clear TRAIN_MARKER_BADGE_PX / 2.
 *
 * Drawn at TRAIN_ARROW_SS x and rendered back down: deck's atlas keeps the native
 * pixels, and a 10 px dart rasterised 1:1 aliases into a smudge.
 */
export const TRAIN_MARKER_BADGE_PX = 24
export const TRAIN_MARKER_ARROW_PX = 23
const TRAIN_ARROW_SS = 2
const TRAIN_ARROW_W = 11
// Canvas rows, in rendered pixels down from the top. The anchor is the BOTTOM edge, so
// distance from the train = TRAIN_MARKER_ARROW_PX − row: the tip stands 22 px out and
// the barbs (the dart's nearest points, and so the ones that decide whether it clears
// the bullet) 13 px, against the bullet's 12 px radius.
//
// The dart is deliberately smaller than the bullet it rides with. It is one bit of
// information (which way) beside three or four (which line), and drawn at the bullet's
// own weight the pair reads as two markers rather than one marker with a heading.
const TRAIN_ARROW_TIP_ROW = 1
const TRAIN_ARROW_BARB_ROW = 10

export type TrainArrowIcon = { url: string; width: number; height: number; anchorY: number }
export const trainArrowCache = new Map<string, TrainArrowIcon>()
export function createTrainHeadingArrow(routeId: string): TrainArrowIcon {
  const cached = trainArrowCache.get(routeId)
  if (cached) return cached
  const s = TRAIN_ARROW_SS
  const canvas = document.createElement('canvas')
  canvas.width = TRAIN_ARROW_W * s
  canvas.height = TRAIN_MARKER_ARROW_PX * s
  const ctx = canvas.getContext('2d')!
  ctx.scale(s, s)
  // A dart, not a triangle: the notched tail reads as an arrowhead at 10 px where a
  // plain triangle reads as a blob. The white casing is the bullet's own ring stroke,
  // so the pair reads as one marker over a busy basemap, but thinner than the bullet's,
  // because a 1 px line around a 10 px dart is already a third of its area.
  const cx = TRAIN_ARROW_W / 2
  ctx.beginPath()
  ctx.moveTo(cx, TRAIN_ARROW_TIP_ROW)
  ctx.lineTo(TRAIN_ARROW_W - 1, TRAIN_ARROW_BARB_ROW)
  ctx.lineTo(cx, TRAIN_ARROW_BARB_ROW - 2.5) // the tail's notch, cut back toward the tip
  ctx.lineTo(1, TRAIN_ARROW_BARB_ROW)
  ctx.closePath()
  ctx.fillStyle = getRouteColor(routeId)
  ctx.fill()
  ctx.strokeStyle = '#fff'
  ctx.lineWidth = 1
  ctx.lineJoin = 'round'
  ctx.stroke()
  const icon = {
    url: canvas.toDataURL(),
    width: canvas.width,
    height: canvas.height,
    anchorY: canvas.height, // bottom edge → the dart sits ahead of the train, never on it
  }
  trainArrowCache.set(routeId, icon)
  return icon
}

// Floating route badge for a bus: the bus counterpart of createTrainBadgeUrl. Bus route
// ids are longer alphanumerics (e.g. "B41", "Q44+", "SIM1", "BxM1"), so it's a colored
// rounded-rect pill sized to the full id rather than a single-glyph disc. Cached per route
// (url + pixel dims, since width varies with the id length, which the IconLayer needs).
export const busBadgeCache = new Map<string, { url: string; width: number; height: number }>()
export function createBusBadge(routeId: string): { url: string; width: number; height: number } {
  const cached = busBadgeCache.get(routeId)
  if (cached) return cached
  const label = routeId || '?'
  const H = 24, PAD = 8, FONT = canvasBulletFont(14)
  const canvas = document.createElement('canvas')
  let ctx = canvas.getContext('2d')!
  ctx.font = FONT
  const W = Math.ceil(ctx.measureText(label).width) + PAD * 2
  canvas.width = W; canvas.height = H        // resizing clears + resets the context
  ctx = canvas.getContext('2d')!
  const r = 6
  ctx.fillStyle = getRouteColor(routeId)
  ctx.beginPath()
  ctx.moveTo(r, 0)
  ctx.arcTo(W, 0, W, H, r)
  ctx.arcTo(W, H, 0, H, r)
  ctx.arcTo(0, H, 0, 0, r)
  ctx.arcTo(0, 0, W, 0, r)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#fff'
  ctx.font = FONT
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(label, W / 2, H / 2 + 0.5)
  const out = { url: canvas.toDataURL(), width: W, height: H }
  busBadgeCache.set(routeId, out)
  return out
}


// Floating badge for an NJ Transit bus: the public route number ("113", not the feed's
// internal "12") on the MTA bus badge's pill — it IS a bus — but in NJ Transit blue with an
// orange band down its leading edge, the agency's own two colours. Under "All" an MTA M5 and
// an NJ Transit 5 can be on screen together, and the band is what tells them apart before
// anyone reads a number.
//
// Keyed by route, colour AND label: both arrive from /api/njbus/network, and a badge drawn
// before it (printing the raw id) must not be the one served once it has.
//
// `estimated` draws it HOLLOW — white, outlined and lettered in the route colour, band kept —
// for a bus whose GPS has gone quiet and is being carried along its timetable
// (njBusMotion.ts). A guess must never read as a sighting, and an icon variant marks it
// without a second layer (a separate layer for a subset is built from nothing every time
// that subset goes from empty to not, and blinks while it loads).
export const njBusBadgeCache = new Map<string, { url: string; width: number; height: number }>()
export function createNjBusBadge(routeId: string, estimated = false): { url: string; width: number; height: number } {
  const color = getRouteColor(routeId)
  const label = routeBadgeLabel(routeId) || '?'
  const key = `${routeId}|${color}|${label}|${estimated ? 'est' : ''}`
  const cached = njBusBadgeCache.get(key)
  if (cached) return cached
  const H = 24, PAD = 7, BAND = 5, FONT = canvasBulletFont(label.length <= 3 ? 14 : 12)
  const scale = 2
  const canvas = document.createElement('canvas')
  let ctx = canvas.getContext('2d')!
  ctx.font = FONT
  const W = Math.ceil(ctx.measureText(label).width) + PAD * 2 + BAND
  canvas.width = W * scale; canvas.height = H * scale  // resizing clears + resets the context
  ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)
  const r = 6
  const outline = () => {
    ctx.beginPath()
    ctx.moveTo(r, 0)
    ctx.arcTo(W, 0, W, H, r)
    ctx.arcTo(W, H, 0, H, r)
    ctx.arcTo(0, H, 0, 0, r)
    ctx.arcTo(0, 0, W, 0, r)
    ctx.closePath()
  }
  outline()
  ctx.fillStyle = estimated ? '#fff' : color
  ctx.fill()
  ctx.save()
  outline()
  ctx.clip()
  ctx.fillStyle = Colors.njbusAccent
  ctx.fillRect(0, 0, BAND, H)
  ctx.restore()
  outline()
  // Hollow: the route colour moves from the fill to a heavier rim and the lettering.
  ctx.lineWidth = estimated ? 2.5 : 1.5
  ctx.strokeStyle = estimated ? color : '#fff'
  ctx.stroke()
  ctx.fillStyle = estimated ? color : getRouteTextColor(routeId)
  ctx.font = FONT
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(label, BAND + (W - BAND) / 2, H / 2 + 0.5)
  const out = { url: canvas.toDataURL(), width: W * scale, height: H * scale }
  njBusBadgeCache.set(key, out)
  return out
}

// Floating badge for a ferry: the route's short name ("ER", "SG") on a rounded SQUARE in the
// route's own NYC Ferry colour, ringed in white. The shape is the point — a subway bullet
// is a disc and a bus badge is a pill, so a square reads as "not the MTA" before the letters
// do, and this map puts all three on screen at once under the "All" filter.
//
// Keyed by route AND colour: the colour arrives from /api/ferry/network, and a badge drawn
// before it (in the fallback navy) must not be the one served once it has.
const FERRY_BADGE_PX = 26
export const ferryBadgeCache = new Map<string, { url: string; width: number; height: number }>()
export function createFerryBadge(routeId: string): { url: string; width: number; height: number } {
  const color = getRouteColor(routeId)
  const key = `${routeId}|${color}`
  const cached = ferryBadgeCache.get(key)
  if (cached) return cached
  const D = FERRY_BADGE_PX
  const scale = 2
  const canvas = document.createElement('canvas')
  canvas.width = D * scale; canvas.height = D * scale
  const ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)
  const r = 6, inset = 1
  ctx.beginPath()
  ctx.moveTo(inset + r, inset)
  ctx.arcTo(D - inset, inset, D - inset, D - inset, r)
  ctx.arcTo(D - inset, D - inset, inset, D - inset, r)
  ctx.arcTo(inset, D - inset, inset, inset, r)
  ctx.arcTo(inset, inset, D - inset, inset, r)
  ctx.closePath()
  ctx.fillStyle = color
  ctx.fill()
  ctx.lineWidth = 1.5
  ctx.strokeStyle = '#fff'
  ctx.stroke()
  const label = routeBadgeLabel(routeId)
  ctx.fillStyle = getRouteTextColor(routeId)
  ctx.font = canvasBulletFont(label.length <= 2 ? 12 : 9)
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(label, D / 2, D / 2 + 0.5)
  const out = { url: canvas.toDataURL(), width: D * scale, height: D * scale }
  ferryBadgeCache.set(key, out)
  return out
}

// Floating badge for a LIRR / Metro-North train: the branch code ("BY", "HA") on a near-
// square-cornered landscape tag in the branch colour, ringed in white, with a white bar
// across its top. The shape is the point, as it is for the ferry's square: subway bullets
// are discs, bus badges pills, ferries rounded squares — and a railroad is none of those,
// even under a filter that puts all four on screen at once.
//
// Keyed by route AND colour: the colour arrives from /api/rail/network, and a badge drawn
// before it (in the fallback blue) must not be the one served once it has.
const RAIL_BADGE_W = 32
const RAIL_BADGE_H = 24
export const railBadgeCache = new Map<string, { url: string; width: number; height: number }>()
export function createRailBadge(routeId: string): { url: string; width: number; height: number } {
  const color = getRouteColor(routeId)
  const key = `${routeId}|${color}`
  const cached = railBadgeCache.get(key)
  if (cached) return cached
  const W = RAIL_BADGE_W, H = RAIL_BADGE_H
  const scale = 2
  const canvas = document.createElement('canvas')
  canvas.width = W * scale; canvas.height = H * scale
  const ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)
  const r = 2.5, inset = 1
  const outline = () => {
    ctx.beginPath()
    ctx.moveTo(inset + r, inset)
    ctx.arcTo(W - inset, inset, W - inset, H - inset, r)
    ctx.arcTo(W - inset, H - inset, inset, H - inset, r)
    ctx.arcTo(inset, H - inset, inset, inset, r)
    ctx.arcTo(inset, inset, W - inset, inset, r)
    ctx.closePath()
  }
  outline()
  ctx.fillStyle = color
  ctx.fill()
  ctx.save()
  outline()
  ctx.clip()
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, W, 4)
  ctx.restore()
  outline()
  ctx.lineWidth = 1.5
  ctx.strokeStyle = '#fff'
  ctx.stroke()
  const label = routeBadgeLabel(routeId)
  ctx.fillStyle = getRouteTextColor(routeId)
  ctx.font = canvasBulletFont(label.length <= 2 ? 12 : 9)
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
  ctx.fillText(label, W / 2, H / 2 + 2.5)
  const out = { url: canvas.toDataURL(), width: W * scale, height: H * scale }
  railBadgeCache.set(key, out)
  return out
}
